import { useEffect, useState } from 'react';
import type { SidePanelView } from '@/components/desktop-side-panel';
import { shownPanelChanged, syncPanelLayouts } from './panel-layout-sync';
import { panelLayoutOf, readStoredPanelLayouts, writeStoredPanelLayouts } from './panel-layouts';
import { createDesktopShellStore } from './store';

/** How long the panels wait to be remembered after a change (resizing changes them often). */
const PANEL_LAYOUT_WRITE_DELAY_MS = 500;

export function useInitialDesktopShellStore(
  initialChatId?: string,
  initialPanelView?: SidePanelView,
  initialWorkspace?: string,
  rememberPanels = false,
) {
  const [store] = useState(() =>
    createDesktopShellStore({
      ...(initialChatId === undefined ? {} : { initialChatId }),
      ...(initialPanelView === undefined
        ? {}
        : { initialSidePanelOpen: true, initialSidePanelView: initialPanelView }),
      ...(initialWorkspace === undefined ? {} : { initialWorkspace }),
      ...(rememberPanels ? { initialPanelLayouts: readStoredPanelLayouts() } : {}),
    }),
  );
  // Each conversation's panel is remembered on this device, the shown one last.
  useEffect(() => {
    if (!rememberPanels) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const write = () => {
      timer = null;
      const state = store.getState();
      writeStoredPanelLayouts({ ...state.panelLayouts, [state.chatId]: panelLayoutOf(state) });
    };
    const unsubscribe = store.subscribe((state, previous) => {
      const changed =
        state.panelLayouts !== previous.panelLayouts ||
        state.chatId !== previous.chatId ||
        shownPanelChanged(state, previous);
      if (changed && timer === null) timer = setTimeout(write, PANEL_LAYOUT_WRITE_DELAY_MS);
    });
    // A saved conversation's panel goes with it, to the user's other devices.
    const stopSync = syncPanelLayouts(store);
    return () => {
      unsubscribe();
      stopSync();
      if (timer !== null) {
        clearTimeout(timer);
        write();
      }
    };
  }, [rememberPanels, store]);
  return store;
}
